// =====================================================================
// src/screens/v2/BizOnlyHome.jsx
//
// Phase 2.4 C9 — "Biz-only home" (Screen H from the onboarding prototype).
//
// Shown when an onboarded user has zero batches: instead of V1's empty
// Home (which assumes an active group), we render a V2 home anchored on
// "Find or create a batch" + the user's business connections + a
// discovery tile for the local-business directory.
//
// This is the landing screen for the meaningful chunk of users who
// arrived via a /biz/CODE deep link, signed up, connected to one
// business, and then went home without joining a batch. Without this
// screen they hit V1's empty-state which doesn't reflect their actual
// usage of the app.
//
// Composition:
//   - Top bar (logo + notifications bell + chat icon)
//   - Greeting: "Hi {firstName} 👋  Welcome to Aangan"
//   - Find-batch card (the V2 differentiator vs pure-business apps)
//   - HomeConnectListV2 — reused as-is; renders the user's biz connections
//   - Discover-more tile → V2 biz directory
//   - Info pill explaining the empty-feed state
//
// Bottom navigation (Home / Members / Profile) is V1's TabBar, kept by
// the parent — this component renders only the content area.
//
// Once the user joins any batch, App.jsx unmounts this and renders the
// regular V1 Home with the group's wall. No state inside BizOnlyHome
// needs to persist across that transition.
// =====================================================================

import React from 'react';
import { Bell, MessageCircle, Users, ArrowRight, Home as HomeIcon, ChevronRight, Info } from 'lucide-react';
import HomeConnectListV2 from './HomeConnectListV2';

// Inline copy of V1 App.jsx's Logo (it's defined inline there, not
// exported as a module). Kept in sync by hand — the SVG hasn't changed
// since launch, so this is acceptably stable.
const Logo = ({ size = 32, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M6 28 V14 Q6 6 16 6 Q26 6 26 14 V28"
          stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="12.5" cy="22" r="1.6" fill={color} />
    <circle cx="19.5" cy="22" r="1.6" fill={color} />
    <circle cx="16" cy="17" r="1.6" fill={color} />
  </svg>
);

export default function BizOnlyHome({
  currentUser,
  onFindBatch,
  onOpenChats,
  onOpenNotifs,
  onOpenBizDirectory,
  unreadNotifs = 0,
  unreadChats = 0,
}) {
  // First name only — the prototype shows "Hi Anjali 👋" not "Hi Anjali
  // Sharma 👋". Falls back to "there" if no name set (shouldn't happen
  // for an onboarded user but defensive).
  const firstName = (currentUser?.name || '').trim().split(/\s+/)[0] || 'there';

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Top bar — mirrors V1 home's structure (Aangan logo left, action
          icons right) so the TabBar transition between this and V1 Home
          doesn't feel jarring when the user eventually joins a batch. */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'var(--ink)' }}>
            <Logo size={18} color="var(--bg)" />
          </div>
          <span className="text-[17px]" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Aangan
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onOpenChats}
            className="relative w-9 h-9 rounded-full flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Open chats"
          >
            <MessageCircle size={20} style={{ color: 'var(--ink)' }} />
            {unreadChats > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: 'var(--accent)' }} />
            )}
          </button>
          <button
            onClick={onOpenNotifs}
            className="relative w-9 h-9 rounded-full flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Open notifications"
          >
            <Bell size={20} style={{ color: 'var(--ink)' }} />
            {unreadNotifs > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: 'var(--accent)' }} />
            )}
          </button>
        </div>
      </div>

      {/* Greeting — bigger and more welcoming than V1's because this is
          the user's permanent landing until they join a batch. */}
      <div className="px-4 pt-3 pb-4 shrink-0">
        <div className="text-[26px] leading-tight" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
          Hi {firstName} <span aria-hidden="true">👋</span>
        </div>
        <div className="text-[14px] mt-0.5" style={{ color: 'var(--ink-soft)' }}>
          Welcome to Aangan
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-6">
        {/* Find batch card — Aangan's primary differentiator. Most
            prominent surface on this screen so users understand the
            social-batch dimension isn't optional, just deferred. */}
        <div
          className="mx-4 mb-5 rounded-2xl p-5"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          >
            <Users size={22} />
          </div>
          <div className="text-[17px] mb-1.5" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontWeight: 500 }}>
            Reconnect with your batchmates
          </div>
          <p className="text-[13px] mb-4 leading-snug" style={{ color: 'var(--ink-soft)' }}>
            Find your school, college, or company batch to see batchmates' posts and updates.
          </p>
          <button
            onClick={onFindBatch}
            className="w-full py-2.5 rounded-full text-[14px] font-medium flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform"
            style={{ background: 'var(--ink)', color: '#fff' }}
          >
            Find or create a batch <ArrowRight size={14} />
          </button>
        </div>

        {/* Connect list — reused from V1 home's V2-flag mode. Renders
            nothing visually when the user has zero connections, which
            is fine: the find-batch card above and the discover tile
            below carry the screen on their own. */}
        <HomeConnectListV2 currentUser={currentUser} />

        {/* Discover more — Local Businesses tile. Routes to the V2 biz
            directory via the existing ?v2=biz-directory URL pattern.
            We don't show a business count in copy because we don't have
            a cheap query for "businesses in this city" at this layer —
            the directory itself surfaces the count once loaded. */}
        <div className="px-4 mt-2 mb-3">
          <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-soft)' }}>
            Discover more
          </div>
        </div>
        <button
          onClick={onOpenBizDirectory}
          className="mx-4 mb-5 rounded-2xl p-4 flex items-center gap-3 w-[calc(100%-32px)] active:scale-[0.995] transition-transform text-left"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          >
            <HomeIcon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px]" style={{ color: 'var(--ink)', fontWeight: 500 }}>
              Local Businesses
            </div>
            <div className="text-[12px] mt-0.5" style={{ color: 'var(--ink-soft)' }}>
              Browse verified businesses near you
            </div>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--ink-soft)' }} />
        </button>

        {/* Empty-feed explainer — softens the absence of posts that V1
            home would normally show. Without this, an onboarded user
            on biz-only home might wonder where the social feed is. */}
        <div
          className="mx-4 rounded-xl px-3.5 py-3 flex gap-2"
          style={{ background: 'var(--info-soft, #EEF3FB)', border: '1px solid rgba(63,107,196,0.15)' }}
        >
          <Info size={14} style={{ color: 'var(--info, #3F6BC4)', flexShrink: 0, marginTop: 1 }} />
          <p className="text-[12px] leading-snug" style={{ color: 'var(--info, #3F6BC4)' }}>
            Join a batch to see <strong>posts, photos, and updates</strong> from your batchmates here.
          </p>
        </div>
      </div>
    </div>
  );
}
